/**
 * §74: готовый ролик — превью, сборка, скачивание.
 *
 * Панель показывает три разных состояния одним блоком: собрать ещё нельзя,
 * сборка идёт, ролик готов. Причина, по которой кнопка неактивна, пишется
 * рядом с кнопкой — искать её в другом месте экрана пользователь не должен.
 */
import { Download, Film, RefreshCw } from 'lucide-react';
import type { ProjectRenderView } from '@alterium/shared';
import { Button } from '../ui/Button';
import { Alert } from '../ui/Alert';
import { noticeText } from '../../i18n/notice';
import { useTranslation } from 'react-i18next';

interface RenderPanelProps {
  render: ProjectRenderView;
  busy: boolean;
  error: string | null;
  onRender: () => void;
  onCancel: () => void;
  onDownload: () => void;
}

export function RenderPanel({
  render,
  busy,
  error,
  onRender,
  onCancel,
  onDownload,
}: RenderPanelProps) {
  const { t } = useTranslation();
  const working = render.status === 'queued' || render.status === 'rendering';
  const blocked = render.blockers.length > 0;

  return (
    <section className="flex flex-col gap-4 rounded-card border border-line bg-surface p-5">
      <div className="flex items-center justify-between">
        <p className="text-card-title">{t('render.title')}</p>
        <span className="text-meta text-content-muted">{t(`enum.renderStatus.${render.status}`, { defaultValue: render.statusLabel })}</span>
      </div>

      {render.videoUrl ? (
        <video
          key={render.videoUrl}
          src={render.videoUrl}
          poster={render.posterUrl ?? undefined}
          controls
          playsInline
          className="w-full rounded-control bg-surface-secondary"
        />
      ) : (
        <div className="flex aspect-[9/16] max-h-[280px] items-center justify-center rounded-control border border-dashed border-line">
          <Film className="h-6 w-6 text-content-muted" strokeWidth={1.5} />
        </div>
      )}

      {working ? (
        <div className="flex flex-col gap-2">
          <div className="h-1 overflow-hidden rounded-full bg-surface-secondary">
            <div
              className="h-full bg-accent transition-[width] duration-slow"
              style={{ width: `${Math.max(4, render.progress)}%` }}
            />
          </div>
          <p className="text-meta text-content-secondary">{t('render.inProgress')}</p>
        </div>
      ) : null}

      {error ? <Alert tone="error">{error}</Alert> : null}

      {/* Ролик собран, но проект после этого правили. */}
      {!working && render.status === 'ready' && !render.upToDate ? (
        <p className="rounded-control border border-line bg-surface-secondary px-3 py-2.5 text-meta text-content-secondary">{t('render.stale')}</p>
      ) : null}

      {!working && blocked ? (
        <p className="text-meta text-content-secondary">{noticeText(render.blockers[0]!)}</p>
      ) : null}

      <div className="flex flex-col gap-2">
        {working ? (
          <Button variant="tertiary" onClick={onCancel}>{t('render.cancel')}</Button>
        ) : (
          <Button
            variant={render.status === 'ready' && render.upToDate ? 'secondary' : 'primary'}
            disabled={busy || blocked}
            onClick={onRender}
          >
            {render.status === 'ready' ? (
              <>
                <RefreshCw className="h-4 w-4" strokeWidth={1.75} />{t('render.again')}</>
            ) : (
              t('render.build', { count: render.units })
            )}
          </Button>
        )}

        {render.videoUrl ? (
          <Button variant="tertiary" onClick={onDownload}>
            <Download className="h-4 w-4" strokeWidth={1.75} />{t('render.download')}</Button>
        ) : null}
      </div>
    </section>
  );
}
